/* start - flights_web_fd_baggage_transparency */
import { useCallback } from "react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { UIFlightData, UIIncludedProductSegment, UILuggageType } from "@flights/types";
import {
  BagHoldNotAvailableIcon,
  BagPersonalItemNotAvailableIcon,
  CabinTrolleyNotAvailableIcon
} from "@bookingcom/bui-assets-react/streamline";
import { SVG } from "@bookingcom/bui-react/components/Icon/Icon.types";
import { INFANT_MAX_AGE } from "@flights/constants";
import useProfiler from "../../../../../../../hooks/useProfiler";

const luggageTypes: UILuggageType[] = ["PERSONAL_ITEM", "HAND", "CHECKED_IN"];

const useDetailedBaggageInfoController = (flight: UIFlightData) => {
  const i18n = useI18n();
  const { isAmerican } = useProfiler();

  const { segments, baggagePolicies } = flight;

  const getSegmentTitle = useCallback(
    (index: number) =>
      i18n.trans(
        t("flights_details_route", {
          variables: {
            city_name: segments[index].arrivalAirport.cityName || segments[index].arrivalAirport.name
          }
        })
      ),
    [i18n, segments]
  );

  const getLuggageData = useCallback(
    ({ segment, differBySegment }: { segment: UIIncludedProductSegment; differBySegment: boolean }) =>
      luggageTypes.map((type) => {
        const includedBaggage = segment.find((s) => s.luggageType === type);

        if (includedBaggage) {
          return { type, includedBaggage };
        }

        const isAvailableAsAncillary = getAvailabilityAsAncillary(flight, type);

        if (isAvailableAsAncillary === "NOT_AVAILABLE") {
          return { type, differBySegment };
        }

        return undefined;
      }),
    [flight]
  );

  const getLuggageTypeData = useCallback(
    ({ luggageType, perSegment }: { luggageType: UILuggageType; perSegment: boolean }) => {
      const luggagesMap: Record<UILuggageType, { icon: SVG; text: string }> = {
        PERSONAL_ITEM: {
          icon: BagPersonalItemNotAvailableIcon,
          text: perSegment
            ? i18n.trans(t("flights_fd_baggage_personal_not_available_flight"))
            : i18n.trans(t("flights_fd_baggage_personal_not_available_booking"))
        },
        HAND: {
          icon: CabinTrolleyNotAvailableIcon,
          text: perSegment
            ? i18n.trans(t("flights_fd_baggage_cabin_not_available_flight"))
            : i18n.trans(t("flights_fd_baggage_cabin_not_available_booking"))
        },
        CHECKED_IN: {
          icon: BagHoldNotAvailableIcon,
          text: perSegment
            ? i18n.trans(t("flights_fd_baggage_checked_not_available_flight"))
            : i18n.trans(t("flights_fd_baggage_checked_not_available_booking"))
        }
      };

      return luggagesMap[luggageType];
    },
    [i18n]
  );

  return {
    header: i18n.trans(t("flights_fd_baggage_heading")),
    subheader: i18n.trans(t("flights_fd_baggage_subheading")),
    getSegmentTitle,
    getLuggageData,
    hasInfantWithNoBaggageAllowance: isInfantWithNoBaggageAllowance(flight),
    baggagePolicies: baggagePolicies && isAmerican() ? baggagePolicies : undefined,
    getLuggageTypeData,
    infantMissingLuggageData: {
      icon: BagHoldNotAvailableIcon,
      text: i18n.trans(t("flights_fd_baggage_infant_no_allowance"))
    }
  };
};

/*
  Checks if baggage type is available as ancillary
*/
export const getAvailabilityAsAncillary = (flight: UIFlightData, baggageType: UILuggageType) => {
  const ancillaries = flight.ancillaries;

  if (!ancillaries) {
    return "NOT_AVAILABLE";
  }

  if (baggageType === "CHECKED_IN") {
    const baggageAncillary = ancillaries.checkedInBaggage;

    if (!baggageAncillary) {
      return "NOT_AVAILABLE";
    }

    return "AVAILABLE";
  }

  if (baggageType === "HAND") {
    const baggageAncillary = ancillaries.cabinBaggagePerTraveller;

    if (!baggageAncillary) {
      return "NOT_AVAILABLE";
    }

    return "AVAILABLE";
  }

  return "NOT_AVAILABLE";
};

/*
  Checks if infants have no baggage whatsoever (not included and not available as ancillary)
*/
export const isInfantWithNoBaggageAllowance = (flight: UIFlightData) => {
  const infant = flight?.travellers?.find(
    (traveller) => traveller.type === "KID" && traveller.age != undefined && traveller.age <= INFANT_MAX_AGE
  );

  if (!infant) {
    return false;
  }

  const hasBaggageInAnySegment = flight.includedProductsBySegment?.reduce((acc, segment) => {
    const found = segment.some((s) => s.travellerReference === infant.travellerReference);

    if (found) {
      acc = true;
    }

    return acc;
  }, false);

  if (hasBaggageInAnySegment) {
    return false;
  }

  const hasCheckedInBaggageAncillary = flight.ancillaries?.checkedInBaggage?.options[0].travellers.includes(
    infant.travellerReference
  );

  if (hasCheckedInBaggageAncillary) {
    return false;
  }

  const hasCabinBaggageAncillary = flight.ancillaries?.cabinBaggagePerTraveller?.travellers.includes(
    infant.travellerReference
  );

  if (hasCabinBaggageAncillary) {
    return false;
  }

  return true;
};

export default useDetailedBaggageInfoController;
/* end - flights_web_fd_baggage_transparency */
